import Head from 'next/head';
import React from 'react';
import getConfig from 'next/config';
import Script from 'next/script';
import JsonLD from 'components/__atoms/json-ld';
import { ensureURL } from '@/utils/functions';

/**
 * Atom PageMetaContent
 *
 * This component adds the meta data and structure
 * to the page's header
 */
function PageMetaContent(props = {}) {

  const { publicRuntimeConfig } = getConfig();

  const { page, settings = {}, breadcrumb = {} } = props.data || {};
  const { title, description, is404 = false } = (page || {});

  if (!page) {
    return (
      <>
        <Head></Head>
        <div>Page not found!</div>
      </>
    );
  }

  // the title of the page varies from page to page
  // but has the main cms title prepended to it
  const theTitle = (settings.title || 'DEx01 amos+ page') +
    (title ? settings.titleSeparator || ' | ' : '') +
    (title);

  // will use the page description, but will append the main
  // cms description to it as well
  const theDescription = (description || '') + ' ' +
    (settings.description || '');

  // the canonical url uses the server address to have the complete
  // absolut string of the url, including protocol, port, etc
  const domain = ensureURL(
    settings.domain || publicRuntimeConfig?.SERVER_ADDR
  );
  const canonicalURL = ensureURL(
    settings.domain || publicRuntimeConfig?.SERVER_ADDR || '',
    page.autopath
  );

  // the image used in social media to share the page
  const socialImage = page?.image?.public_url ||
    settings?.image?.public_url ||
    null;

  // integrations
  const fb = settings?.integrations?.third_party?.Facebook;
  const google = settings?.integrations?.third_party?.Google;
  const iframes = settings?.integrations?.iframes;
  const metatags = settings?.integrations?.metatags;

  // scripts to be imported or embeded
  const injectedHeadScripts = [];
  const injectedBodyScripts = [];
  const preConnect = [];

  function separateScripts(kind) {
    return scr => {
      scr.kind = kind;

      if (scr.placement === 'head') {
        injectedHeadScripts.push(scr);
      } else {
        injectedBodyScripts.push(scr);
      }

      if (scr.kind === 'import') {
        if (!scr.script.startsWith(domain)) {
          preConnect.push(scr);
        }
      }
    };
  }

  (settings?.integrations?.imports || []).forEach(separateScripts('import'));
  (settings?.integrations?.scripts || []).forEach(separateScripts('embed'));

  function scriptSorter(scrA, scrB) {
    if (scrA.kind === 'import' && scrB.kind !== 'import') {
      return -1;
    }
    return 1;
  }

  return (
    <>
      <Head>
        {
          // scripts imported from different domains should be pre-connected
          (preConnect || [])?.map(scr => {
            return (
              <link
                rel="preconnect"
                href={(new URL(scr.script)).origin}
                key={scr.script}
              />
            );
          })
        }

        {
          /* favicon */
          settings?.icon && (
            <link
              rel="shortcut icon"
              type={settings?.icon.type || 'image/png'}
              href={
                settings?.icon?.public_url ||
                "/images/favicon.png"
              }
              media={
                settings.icon_dark
                  ? "(prefers-color-scheme: light)"
                  : null
              }
            />
          )
        }
        {
          /* dark favicon */
          settings.icon_dark && (
            <link
              rel="shortcut icon"
              href={
                settings?.icon_dark?.public_url ||
                "/images/favicon.png"
              }
              media="(prefers-color-scheme: dark)"
            />
          )
        }

        {/* just to help debugging when needed */}
        <meta name="envtype" content={publicRuntimeConfig.ENV_TYPE} />

        {
          /* meta tag for robots */
          (
            publicRuntimeConfig.ENV_TYPE !== 'PRD' ||
            page.hide_from_robots
          )
            ? <meta name="robots" content="noindex, nofollow" />
            : <meta name="robots" content="all" />
        }

        {/* title */}
        <title>{theTitle}</title>
        <meta name="title" content={theTitle} />
        <meta property="og:title" content={theTitle} />
        <meta property="twitter:title" content={theTitle} />

        {/* description */}
        <meta name="description" content={theDescription} />
        <meta property="og:description" content={theDescription} />
        <meta property="twitter:description" content={theDescription} />

        {/* <meta name="rating" content={
          page.rating // "general", "safe for kids", "14 years" or "adult"
          // general", "PG", "PG-13", "R", "NC-17", "Adult", "E", "T", "M"
          || "general"
        } /> */}

        {canonicalURL && !is404 && (
          <>
            <link rel="canonical" href={canonicalURL} />
            <meta property="og:url" content={canonicalURL} />
            <meta property="twitter:url" content={canonicalURL} />
          </>
        )}

        <meta property="og:type" content="website" />
        <meta property="og:image" itemProp="image" content={socialImage} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:image" content={socialImage} />

        <JsonLD
          type="organization"
          domain={settings?.domain || publicRuntimeConfig?.SERVER_ADDR}
          data={{
            url: settings?.domain || publicRuntimeConfig?.SERVER_ADDR,
            name: settings?.title || '',
            logo: settings?.image?.public_url || '',
            socialNetworks: settings?.social_networks
          }}
        />

        {
          breadcrumb && (breadcrumb.length || Object.keys(breadcrumb).length) && (
            <JsonLD
              domain={settings?.domain || publicRuntimeConfig?.SERVER_ADDR}
              type="breadcrumb"
              data={breadcrumb}
            />
          )
        }

        <JsonLD
          domain={settings?.domain || publicRuntimeConfig?.SERVER_ADDR}
          type="site"
          data={{
            title: settings?.title
          }}
        />

        {
          // scripts to be injected in the page's head
          injectedHeadScripts?.sort(scriptSorter)?.map(scr => {
            if (scr.kind === 'import') {
              return (
                <Script
                  key={scr.name || scr.script}
                  name={scr.name}
                  src={scr.script}
                />
              );
            } else {
              return (
                <Script
                  key={scr.name}
                  id={scr.name}
                  name={scr.name}
                >
                  {`${scr.script?.replace(/`/g, '\\`')}`}
                </Script>
              );
            }
          })
        }

        {
          // the added metatags
          metatags?.map(metatag => {
            return (
              <meta
                key={metatag.name || metatag.property || metatag.content}
                name={metatag?.name || null}
                property={metatag?.property || null}
                content={metatag?.content || null}
              />
            );
          })
        }

      </Head>
      {
        google?.ga && <>
          <Script
            async
            // src={`https://www.googletagmanager.com/gtag/js?id=${google?.ga?.[0]}`}
            src={`https://www.googletagmanager.com/gtm.js?id=${google?.ga?.[0]}`}
          />
          <Script id="google-gtm">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());

              ${google?.ga?.map(gaId => {
              return ` gtag('config', '${gaId}');`;
            })}
            `}
          </Script>
        </>
      }
      {
        fb?.pixelId && (
          <Script
            id='fb-pixel'
            strategy='afterInteractive'
          >
            {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${fb.pixelId}');
            fbq('track', 'PageView');
          `}
          </Script>
        )
      }

      {
        google?.recaptcha && (
          <Script src="https://www.google.com/recaptcha/api.js" async defer />
        )
      }

      {
        google?.maps && (
          <Script id="google-maps">
            {`
            (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src="https://maps."+c+"apis.com/maps/api/js?"+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
              key: "${google?.maps}",
              v: "weekly",
              // Use the 'v' parameter to indicate the version to use (weekly, beta, alpha, etc.).
              // Add other bootstrap parameters as needed, using camel case.
            });
            `}
          </Script>
        )
      }
      {
        google?.recaptcha && (
          <form action="?" method="POST">
            <div className="g-recaptcha" data-sitekey={google?.recaptcha} />
            <br />
            <input type="submit" value="Submit" />
          </form>
        )
      }

      {
        iframes?.map(iframe => {
          return (
            <iframe
              className={'hidden-iframe'}
              key={iframe.url}
              src={iframe.url}
              title={iframe.name}
              name={iframe.name}
              sandbox={iframe.sandboxed}
            />
          );
        })
      }

      {
        // the injected scripts to be added to the page
        injectedBodyScripts?.sort(scriptSorter)?.map(scr => {
          if (scr.kind === 'import') {
            return (
              <Script
                key={scr.name || scr.script}
                name={scr.name}
                src={scr.script}
              />
            );
          } else {
            return (
              <Script
                key={scr.name}
                id={scr.name}
                name={scr.name}
              >
                {`
                try {
                  ${scr.script?.replace(/`/g, '\\`')}
                }catch(error) {
                  console.error('Script with name ${scr.name} triggered an error', error);
                }`}
              </Script>
            );
          }
        })
      }
    </>
  );
}

export default PageMetaContent;
